import qs from 'qs';
import { unflatten } from 'flat';
import { merge } from 'lodash-es';
import { defineStore } from 'pinia';
import { AxiosResponse } from 'axios';

import apiGw from '@/services/apiGw';
import { useI18n, I18nMessages } from '@/plugins/i18n';
import { getPreferredLanguage, getPreferredLocale } from '@/utils/locale';

const i18n = useI18n();

type State = {
  collectionNames: { [key: string]: Set<string> };
  localeMessages: { [key: string]: any };
  pendingPromises: { [key: string]: Promise<AxiosResponse> };
  currentlyLoadingCollections: { [key: string]: Set<string> };
  alpha2ToAlpha3: { [key: string]: string };
  alpha3ToName: { [key: string]: { [key: string]: string } };
};

export const useRefdataStore = defineStore('refdata', {
  state: (): State => ({
    collectionNames: {},
    localeMessages: {},
    pendingPromises: {},
    currentlyLoadingCollections: {},
    alpha2ToAlpha3: {},
    alpha3ToName: {}
  }),
  actions: {
    async loadCollections(collectionNames: Array<string>, config?: { headers: { 'Accept-Language': string } }) {
      const preferredLanguage = config?.headers['Accept-Language'] ?? getPreferredLanguage();
      const promiseKey = `${preferredLanguage}-${collectionNames.join(',')}`;
      if (!!this.pendingPromises[promiseKey]) {
        // In case we are already loading this set of collectionNames, wait for the pending promise to resolve and return as no further work is required.
        await this.pendingPromises[promiseKey];
        return;
      }

      const missingCollectionNames = new Set<string>();
      if (this.collectionNames[preferredLanguage]) {
        collectionNames.forEach((name) => {
          // If the collection is not already loaded and not currently being loaded, add it to the missingCollectionNames set.
          if (
            !this.collectionNames[preferredLanguage].has(name) &&
            !this.currentlyLoadingCollections[preferredLanguage].has(name)
          ) {
            missingCollectionNames.add(name);
          }
        });
      } else {
        this.collectionNames[preferredLanguage] = new Set();
        collectionNames.forEach((name) => missingCollectionNames.add(name));
      }
      if (missingCollectionNames.size) {
        if (!this.localeMessages[preferredLanguage]) {
          this.currentlyLoadingCollections[preferredLanguage] = new Set();
        }
        for (const mcn of missingCollectionNames) {
          this.currentlyLoadingCollections[preferredLanguage].add(mcn);
        }

        const options = {
          names: [...missingCollectionNames]
        };
        // @ts-ignore
        const queryParamsComma = qs.stringify(options, { encode: false, arrayFormat: 'comma', commaRoundTrip: true });
        const promise = apiGw.get(`collections?${queryParamsComma}`, {
          ...{ headers: { 'Accept-Language': preferredLanguage }, ...config }
        });
        this.pendingPromises[promiseKey] = promise;
        const collections: AxiosResponse = await promise;

        // refdata interpollation uses double curly braces {{ while vue-i18n uses single ones..
        makeI18nCompatible(collections.data);

        // Do not create array automatically when calling unflatten (this prevent have huge array of  mostly empty objects when dealing with number keys)
        const messages: I18nMessages = unflatten(collections.data, {
          object: true
        });

        collectionNames.forEach((name) => this.collectionNames[preferredLanguage].add(name));

        this.localeMessages[preferredLanguage] = merge(this.localeMessages[preferredLanguage], messages);
      }

      let locale = getPreferredLocale();
      const currentLocale = i18n.getCurrentLocale();
      if (currentLocale != locale) {
        locale = currentLocale;
      }
      i18n.setLocaleMessage(locale, this.localeMessages[locale.substring(0, 2)]);

      delete this.pendingPromises[promiseKey];
      missingCollectionNames.forEach((name) => this.currentlyLoadingCollections[preferredLanguage].delete(name));
    },
    async countryAlpha2ToAlpha3(alpha2: string): Promise<string> {
      const promiseKey = `country-codes-${alpha2}`;
      if (!!this.pendingPromises[promiseKey]) {
        // In case we are already loading this country code, wait for the pending promise to resolve and return as no further work is required.
        await this.pendingPromises[promiseKey];
      } else if (!this.alpha2ToAlpha3[alpha2]) {
        const promise = apiGw.get(`/collection-lookup/country-codes/${alpha2}`);
        this.pendingPromises[promiseKey] = promise;
        const rsp = await promise;
        this.alpha2ToAlpha3[alpha2] = rsp.data.content;
        delete this.pendingPromises[promiseKey];
      }
      return this.alpha2ToAlpha3[alpha2];
    },
    /**
     * Load only the country name for the given country code without loading the entire `countries` collection.
     * If the `countries` collection is already loaded, this method will not load it again.
     * @param countryCode
     * @param config
     * @returns
     */
    async countryCodeToName(countryCode: string, config?: { headers: { 'Accept-Language': string } }): Promise<string> {
      const preferredLanguage = config?.headers['Accept-Language'] ?? getPreferredLanguage();
      if (this.collectionNames[preferredLanguage].has('countries')) {
        // If the `countries` collection is already loaded, return the country name from the collection.
        return this.localeMessages[preferredLanguage].countries[countryCode];
      }

      const promiseKey = `alpha3ToName-${preferredLanguage}-${countryCode}`;
      if (!!this.pendingPromises[promiseKey]) {
        // In case we are already loading this country code, wait for the pending promise to resolve and return as no further work is required.
        await this.pendingPromises[promiseKey];
      } else if (!this.alpha3ToName[preferredLanguage] || !this.alpha3ToName[preferredLanguage][countryCode]) {
        const promise = apiGw.get(`/collection-lookup/countries/${countryCode}`, {
          ...{ headers: { 'Accept-Language': preferredLanguage }, ...config }
        });
        this.pendingPromises[promiseKey] = promise;
        if (!this.alpha3ToName[preferredLanguage]) {
          this.alpha3ToName[preferredLanguage] = {};
        }
        this.alpha3ToName[preferredLanguage][countryCode] = (await promise).data.content;
        delete this.pendingPromises[promiseKey];
      }
      return this.alpha3ToName[preferredLanguage][countryCode];
    }
  }
});

/**
 * Replace the Double {{}} into single {} as vue-i18n interpolation works only on single {}.
 *
 * @param obj
 * @returns
 */
function makeI18nCompatible(obj: any) {
  if (typeof obj === 'object') {
    Object.keys(obj).forEach((keys) => {
      if (typeof obj[keys] === 'object') {
        makeI18nCompatible(obj[keys]);
      } else {
        const keyValue = obj[keys].replaceAll('{{', '{').replaceAll('}}', '}');
        obj[keys] = keyValue;
      }
    });
  }
}
